import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { AutoFixHigh as AutoFixHighIcon, Save as SaveIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api';
import { useCurrentUser } from '../auth';
import { extractApiErrorMessage, extractApiFieldErrors, isValidationProblem } from '../apiErrors';
import { ENABLED_BASE_LANGUAGE_CODES, getEnabledBaseLanguageOptions } from '../constants/languages';
import { PRODUCT_SDS_ADD_PERMISSION } from '../constants/permissions';
import { GhsPictogramSelector } from '../components/products/GhsPictogramSelector';
import { SdsDropzone } from '../components/products/SdsDropzone';
import { getLanguageDisplayName } from '../components/product-details/utils';
import {
  analyzeStatementXmlSegments,
  StatementXmlEditor,
  serializeStatementXmlSegments,
  statementXmlToPlainText,
  createEmptySentenceRow,
  sentenceRowsToSegments,
  type StatementSegment,
} from '../components/statement-nodes';
import PageHeader from '../components/PageHeader';
import PageBreadcrumbs from '../components/Breadcrumbs';
import SectionCard from '../components/SectionCard';
import { UploadedSdsFile } from '../types/sds';

const SectionTitle: React.FC<{ title: string; hint?: string; action?: React.ReactNode }> = ({ title, hint, action }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Box>
    {action}
  </Stack>
);

// Plain text typed by the user becomes one composer row per non-empty line.
const plainTextToSegments = (text: string): StatementSegment[] => {
  const rows = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => ({ ...createEmptySentenceRow(), text: line }));
  return sentenceRowsToSegments(rows.length > 0 ? rows : [createEmptySentenceRow()]);
};

const NewProduct: React.FC = () => {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const { permissions } = useCurrentUser();
  const enabledBaseLanguageOptions = useMemo(() => getEnabledBaseLanguageOptions(), []);
  const defaultBaseLanguage = enabledBaseLanguageOptions[0]?.code ?? 'en';
  const canAddSds = permissions.includes(PRODUCT_SDS_ADD_PERMISSION);

  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    ean: '',
    base_language: defaultBaseLanguage,
    clp_text: '',
  });
  const [ghsPictograms, setGhsPictograms] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [sdsUploading, setSdsUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [sdsError, setSdsError] = useState<string | null>(null);
  const [sdsWarning, setSdsWarning] = useState<string | null>(null);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [extractWarning, setExtractWarning] = useState<string | null>(null);
  const [uploadedSds, setUploadedSds] = useState<UploadedSdsFile | null>(null);
  const [sdsClpExtractionId, setSdsClpExtractionId] = useState<string | null>(null);
  const [xmlSegments, setXmlSegments] = useState<StatementSegment[] | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; sku?: string; ean?: string; clp_text?: string }>({});

  // Remembers composer content when switching to free text, so toggling back
  // without edits restores tags instead of collapsing everything to plain rows.
  const composerCacheRef = useRef<{ plainText: string; segments: StatementSegment[] } | null>(null);

  const effectiveClpText = useMemo(
    () => (xmlSegments ? serializeStatementXmlSegments(xmlSegments) : formData.clp_text),
    [formData.clp_text, xmlSegments]
  );

  const inputMode = xmlSegments !== null ? 'composer' : 'text';

  const busy = loading || sdsUploading;
  const canSubmit = formData.name.trim().length > 0 && effectiveClpText.trim().length > 0 && !busy && !parsing;
  const canParse = formData.clp_text.trim().length > 0 && !parsing && !busy;

  const handleFormChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'name' || field === 'sku' || field === 'ean' || field === 'clp_text') {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (field === 'clp_text') {
      setXmlSegments(null);
      setParseError(null);
    }
  };

  const handleModeChange = (_: React.MouseEvent, newMode: 'text' | 'composer' | null) => {
    if (!newMode || newMode === inputMode) return;

    if (newMode === 'composer') {
      const currentText = formData.clp_text;
      const cached = composerCacheRef.current;
      if (cached && cached.plainText === currentText) {
        setXmlSegments(cached.segments);
        return;
      }
      if (currentText.trim()) {
        const parsed = analyzeStatementXmlSegments(currentText);
        setXmlSegments(parsed.hasNodes ? parsed.segments : plainTextToSegments(currentText));
      } else {
        setXmlSegments(sentenceRowsToSegments([createEmptySentenceRow()]));
      }
    } else {
      const plain = statementXmlToPlainText(serializeStatementXmlSegments(xmlSegments!));
      composerCacheRef.current = { plainText: plain, segments: xmlSegments! };
      setFormData((prev) => ({ ...prev, clp_text: plain }));
      setXmlSegments(null);
    }
  };

  const handleCancel = () => {
    if (busy) return;
    navigate('/products');
  };

  const convertTextToSegments = async (text: string): Promise<StatementSegment[] | null> => {
    const resp = await api.post('/convert-to-xml', {
      text: text.trim(),
      language: formData.base_language,
    });
    const converted = (resp?.converted_text || resp?.convertedText) as string | undefined;
    if (!converted) return null;
    const parsed = analyzeStatementXmlSegments(converted);
    return parsed.hasNodes ? parsed.segments : null;
  };

  const handleParse = async () => {
    if (!canParse) return;
    setParsing(true);
    setParseError(null);
    try {
      const segments = await convertTextToSegments(formData.clp_text);
      if (segments) {
        composerCacheRef.current = null;
        setXmlSegments(segments);
        setFormData((prev) => ({ ...prev, clp_text: serializeStatementXmlSegments(segments) }));
        setFieldErrors((prev) => ({ ...prev, clp_text: undefined }));
      } else {
        setParseError(t('products.parseFailed'));
      }
    } catch {
      setParseError(t('products.parseFailed'));
    } finally {
      setParsing(false);
    }
  };

  const handleSdsSelected = async (file: File) => {
    if (!canAddSds) return;

    const formDataPayload = new FormData();
    formDataPayload.append('file', file);
    formDataPayload.append('extract_label_text_from_clp', 'false');

    setSdsUploading(true);
    setSdsError(null);
    setSdsWarning(null);
    // A new file invalidates any extraction from a previous one.
    setExtractError(null);
    setExtractWarning(null);
    setSdsClpExtractionId(null);

    try {
      const uploaded = await api.postFormData('/files/sds', formDataPayload);
      setUploadedSds(uploaded);
    } catch (err: unknown) {
      setSdsError(extractApiErrorMessage(err) || t('products.sdsUploadFailed'));
    } finally {
      setSdsUploading(false);
    }
  };

  const handleExtractFromSds = async () => {
    if (!uploadedSds) return;

    const formDataPayload = new FormData();
    formDataPayload.append('source_language', formData.base_language);

    setExtracting(true);
    setExtractError(null);
    setExtractWarning(null);

    try {
      const conversion = await api.postFormData(`/files/sds/${uploadedSds.id}/extract-clp`, formDataPayload);
      setSdsClpExtractionId(conversion?.id || null);
      const extractedClpText = conversion?.extracted_clp_text || '';
      if (extractedClpText) {
        composerCacheRef.current = null;
        const parsed = analyzeStatementXmlSegments(extractedClpText);
        if (parsed.hasNodes) {
          setXmlSegments(parsed.segments);
          setFormData((prev) => ({ ...prev, clp_text: extractedClpText }));
        } else {
          // Extraction returned unstructured text: show it as free text instead
          // of silently doing nothing.
          setXmlSegments(null);
          setFormData((prev) => ({ ...prev, clp_text: extractedClpText }));
          setExtractWarning(t('products.sdsExtractedUnstructured'));
        }
        setFieldErrors((prev) => ({ ...prev, clp_text: undefined }));
        setParseError(null);
      }
      const extractedGhsCodes = conversion?.ghs_codes;
      if (extractedGhsCodes && extractedGhsCodes.length > 0) {
        setGhsPictograms(extractedGhsCodes);
      }
      if (conversion?.status === 'failed' || conversion?.status === 'markdown_conversion_failed') {
        setExtractWarning(conversion.error_message || t('products.sdsConversionFailedWarning'));
      }
    } catch (err: unknown) {
      setExtractError(extractApiErrorMessage(err) || t('products.sdsExtractFailed'));
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = async () => {
    const nextErrors: typeof fieldErrors = {};
    if (!formData.name.trim()) nextErrors.name = t('products.productNameRequired');
    if (!effectiveClpText.trim()) nextErrors.clp_text = t('errors.required');
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setLoading(true);
    setError(null);

    try {
      let clpTextForSave = effectiveClpText.trim();

      // Free-text input is structured automatically on save, so the user never
      // hits a disabled Save button without knowing why.
      if (xmlSegments === null) {
        let segments: StatementSegment[] | null = null;
        try {
          segments = await convertTextToSegments(formData.clp_text);
        } catch {
          segments = null;
        }
        if (!segments) {
          setFieldErrors((prev) => ({ ...prev, clp_text: t('products.parseFailed') }));
          setError(t('products.parseFailed'));
          return;
        }
        composerCacheRef.current = null;
        setXmlSegments(segments);
        clpTextForSave = serializeStatementXmlSegments(segments).trim();
        setFormData((prev) => ({ ...prev, clp_text: clpTextForSave }));
      }

      const baseLanguage = ENABLED_BASE_LANGUAGE_CODES.includes(formData.base_language)
        ? formData.base_language
        : defaultBaseLanguage;

      const productPayload = {
        name: formData.name.trim(),
        sku: formData.sku.trim() || undefined,
        ean: formData.ean.trim() || undefined,
        base_language: baseLanguage,
        base_clp_text: clpTextForSave,
        sds_file_id: uploadedSds?.id || undefined,
        sds_clp_extraction_id: sdsClpExtractionId || undefined,
        ghs_pictograms: ghsPictograms,
      };

      const product = await api.post('/products', productPayload);
      navigate(`/products/${product.id}`);
    } catch (err: unknown) {
      const apiFieldErrors = extractApiFieldErrors(err);
      const mapped: typeof fieldErrors = {};
      if (apiFieldErrors.name) mapped.name = apiFieldErrors.name;
      if (apiFieldErrors.sku) mapped.sku = apiFieldErrors.sku;
      if (apiFieldErrors.ean) mapped.ean = apiFieldErrors.ean;
      if (apiFieldErrors.base_clp_text) mapped.clp_text = apiFieldErrors.base_clp_text;
      setFieldErrors(mapped);

      const hasFieldValidation = Object.keys(mapped).length > 0;
      if (isValidationProblem(err) || hasFieldValidation) {
        setError(t('errors.validationFixFields'));
      } else {
        setError(extractApiErrorMessage(err) || t('errors.fetchFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const actionButtons = (
    <Stack direction="row" spacing={1}>
      <Button onClick={handleCancel} disabled={busy} color="inherit">
        {t('productDetails.cancel')}
      </Button>
      <Button
        onClick={handleSubmit}
        variant="contained"
        disabled={!canSubmit}
        startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
      >
        {loading ? t('products.creating') : t('products.createProduct')}
      </Button>
    </Stack>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <PageBreadcrumbs
        items={[
          { label: t('nav.products'), path: '/products' },
          { label: t('products.newProduct') },
        ]}
      />

      <PageHeader
        title={t('products.newProduct')}
        subtitle={t('products.createProductSubtitle')}
        actions={actionButtons}
        mb={3}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2.5}>
        <SectionCard sx={{ p: 2.5 }}>
          <SectionTitle title={t('products.productName')} />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              label={t('products.productName')}
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              fullWidth
              disabled={loading}
              placeholder={t('products.productNamePlaceholder')}
              error={Boolean(fieldErrors.name)}
              helperText={fieldErrors.name}
              required
              sx={{ flex: 2 }}
            />
            <TextField
              size="small"
              label={t('products.productSku')}
              value={formData.sku}
              onChange={(e) => handleFormChange('sku', e.target.value)}
              fullWidth
              disabled={loading}
              placeholder={t('products.skuPlaceholder')}
              error={Boolean(fieldErrors.sku)}
              helperText={fieldErrors.sku}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label={t('products.productEan')}
              value={formData.ean}
              onChange={(e) => handleFormChange('ean', e.target.value)}
              fullWidth
              disabled={loading}
              placeholder={t('products.eanPlaceholder')}
              error={Boolean(fieldErrors.ean)}
              helperText={fieldErrors.ean}
              sx={{ flex: 1 }}
            />
          </Stack>
        </SectionCard>

        {canAddSds && (
          <SectionCard sx={{ p: 2.5 }}>
            {/* <SectionTitle title={t('products.sdsSectionTitle')} hint={t('products.sdsHelper')} /> */}
            <SdsDropzone
              title={t('products.sdsSectionTitle')}
              buttonLabel={uploadedSds ? t('products.sdsReplaceAction') : t('products.sdsAddAction')}
              helperText={t('products.sdsHelper')}
              currentFileName={uploadedSds?.original_filename}
              loading={sdsUploading}
              error={sdsError}
              warning={sdsWarning}
              hideExtractToggle
              onFileSelected={handleSdsSelected}
            />
          </SectionCard>
        )}

        <SectionCard sx={{ p: 2.5 }}>
          <SectionTitle
            title={t('products.clpText')}
            hint={t('products.clpTextHelper')}
            action={
              <ToggleButtonGroup
                size="small"
                exclusive
                value={inputMode}
                onChange={handleModeChange}
                disabled={busy || parsing || extracting}
              >
                <ToggleButton value="text" sx={{ px: 1.5, py: 0.25, fontSize: '0.75rem', textTransform: 'none' }}>
                  {t('products.clpInputModeText')}
                </ToggleButton>
                <ToggleButton value="composer" sx={{ px: 1.5, py: 0.25, fontSize: '0.75rem', textTransform: 'none' }}>
                  {t('products.clpInputModeComposer')}
                </ToggleButton>
              </ToggleButtonGroup>
            }
          />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              select
              label={t('products.sourceLanguage')}
              value={formData.base_language}
              onChange={(e) => handleFormChange('base_language', e.target.value)}
              disabled={loading || extracting}
              sx={{ minWidth: 220 }}
            >
              {enabledBaseLanguageOptions.map((opt) => (
                <MenuItem key={opt.code} value={opt.code}>
                  {getLanguageDisplayName(opt.code)}
                </MenuItem>
              ))}
            </TextField>
            {canAddSds && (
              <Tooltip title={!uploadedSds ? t('products.sdsExtractHint') : ''}>
                <span>
                  <Button
                    onClick={handleExtractFromSds}
                    variant="outlined"
                    size="small"
                    disabled={!uploadedSds || extracting || busy}
                    startIcon={extracting ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {extracting ? t('products.sdsExtracting') : t('products.sdsExtractButton')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>

          {extractWarning && (
            <Alert severity="warning" onClose={() => setExtractWarning(null)} sx={{ py: 0.5, mb: 1.5 }}>
              {extractWarning}
            </Alert>
          )}
          {extractError && (
            <Alert severity="error" onClose={() => setExtractError(null)} sx={{ py: 0.5, mb: 1.5 }}>
              {extractError}
            </Alert>
          )}

          {inputMode === 'composer' ? (
            <Box>
              {fieldErrors.clp_text && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 0.75 }}>
                  {fieldErrors.clp_text}
                </Typography>
              )}
              <StatementXmlEditor
                segments={xmlSegments ?? []}
                onSegmentsChange={(nextSegments) => {
                  setXmlSegments(nextSegments);
                  setFormData((prev) => ({
                    ...prev,
                    clp_text: serializeStatementXmlSegments(nextSegments),
                  }));
                  setFieldErrors((prev) => ({ ...prev, clp_text: undefined }));
                }}
                language={formData.base_language}
                disabled={busy}
              />
            </Box>
          ) : (
            <Stack spacing={1}>
              <TextField
                value={formData.clp_text}
                onChange={(e) => handleFormChange('clp_text', e.target.value)}
                fullWidth
                multiline
                minRows={8}
                disabled={busy || parsing}
                placeholder={t('products.clpTextPlaceholder')}
                helperText={fieldErrors.clp_text || (formData.clp_text.trim() ? t('products.clpTextParseRequired') : undefined)}
                error={Boolean(fieldErrors.clp_text)}
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                  },
                }}
              />
              {parseError && (
                <Alert severity="warning" onClose={() => setParseError(null)}>
                  {parseError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  onClick={handleParse}
                  variant="outlined"
                  size="small"
                  disabled={!canParse}
                  startIcon={parsing ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                >
                  {parsing ? t('products.parsing') : t('products.parseClpText')}
                </Button>
              </Box>
            </Stack>
          )}
        </SectionCard>

        <SectionCard sx={{ p: 2.5 }}>
          <SectionTitle title={t('ghsPictograms.sectionTitle')} />
          <GhsPictogramSelector value={ghsPictograms} onChange={setGhsPictograms} />
        </SectionCard>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
        {actionButtons}
      </Stack>
    </Box>
  );
};

export default NewProduct;
